/**
 * Phone-number helpers for the Manual Checkout (TXAM) form.
 *
 * (2026-10-23, Jason Cox) Added with the required "Picker phone" field. The
 * backend (rebound-smart ManualCheckoutNote::normalizePhone) keeps only phone
 * characters and caps the stored value at 20, so the limits here match it.
 */

/** Longest phone value the backend stores (e.g. "+1 (432) 555-1234 x12"). */
export const PICKER_PHONE_MAX_LENGTH = 20;

/**
 * Per-field cap for the free-text checkout fields stored in the ticket note
 * (Associated Party, Foreman, picker name, picker company). Matches the
 * backend's ManualCheckoutNote::TEXT_MAX so nothing typed is silently cut off.
 */
export const CHECKOUT_TEXT_MAX_LENGTH = 60;

/**
 * Count the digits in a phone value, ignoring punctuation and spaces.
 *
 * @param value - Raw user input.
 * @returns Number of 0-9 characters.
 */
export function countPhoneDigits(value: string): number {
  return (value.match(/\d/g) || []).length;
}

/**
 * Whether a typed phone number is complete enough to call back.
 *
 * Accepts any formatting -- "4325551234", "(432) 555-1234", "+1 432 555 1234",
 * "432-555-1234 x12" -- as long as it has 10 to 15 digits.
 * Reason: 10 is a US number without country code (the TXAM crews are US-based);
 * 15 is the international maximum, and leaves room for a short extension.
 *
 * @param value - Raw user input.
 * @returns true when the value has a plausible number of digits.
 */
export function isValidPhone(value: string): boolean {
  const digits = countPhoneDigits(value);
  return digits >= 10 && digits <= 15;
}
